import { createSelector } from "@reduxjs/toolkit";
import {
  ATTACHED_TO_LENGTH_COMP_CLASSES,
  DEVICES_TYPES_SETTINGS,
  DeviceCategoryEnum,
  LENGTH_COMP_CLASSES,
  buildDimensionLinesFromPointToPolygonSides,
  getDeeplyNestedValue,
  getDeviceCategoryForMcBimItem,
  getMcbimItemClassificationInput,
} from "@multiconsult-group/multiplacer-core";
import { IPixDimensionLine, IPixPath, IPixPolygon } from "@multiconsult-group/multiplacer-ui";
import { buildCeilingGridPixPathsFromLines } from "../../buildCeilingGridPixPathsFromLines";
import { getHeightFromFirstCeilingGrid } from "../../DesignTab/designSlice";
import { convertMcBimSpaceToPixPolygon } from "../../DesignTab/pixpolygonSelectors";
import { openapi } from "../../../openapi";
import { IRootState } from "../../app/store";
import { selectCompCodePath, selectMmiPath } from "../../globalSelectors";
import { selectProjectNo } from "../../sharedSelectors";
import { selectHttpSingleRoomFull } from "../../Room/httpRoomSelectors";
import { IMcBimItemWithExtraFrontendInfo } from "../../types/types";
import { selectActiveDeviceInSelectedRoom } from "../../DesignTab/devices/deviceSlice";
import {
  selectStandardRoomShowOnlyCeilingComponents,
  selectStandardRoomShowFurniture,
  selectStandardRoomShowLengthComponents,
} from "./standardRoomSlice";

const getMcbimItemDrawZ = (item: IMcBimItemWithExtraFrontendInfo): number => {
  const zRel = item.geometry?.zRelMm;
  if (typeof zRel?.middle === "number") return zRel.middle;
  if (typeof zRel?.bottom === "number") return zRel.bottom;
  if (typeof zRel?.top === "number") return zRel.top;

  const zWorld = item.geometry?.zWorldMm;
  if (typeof zWorld?.middle === "number") return zWorld.middle;
  if (typeof zWorld?.bottom === "number") return zWorld.bottom;
  if (typeof zWorld?.top === "number") return zWorld.top;

  return 0;
};

export const selectActiveStandardRoomName = createSelector(
  [selectHttpSingleRoomFull],
  (room): string | null => {
    if (room?.rfpMalRom == null || room.rfpMalRom === "") {
      return null;
    }

    return room.rfpMalRom;
  }
);

export const selectActiveStandardRoomMcbimSpaceArgs = createSelector(
  [selectProjectNo, selectActiveStandardRoomName],
  (projectNo, activeStandardRoomName) => {
    if (!projectNo || !activeStandardRoomName) {
      return null;
    }

    return {
      projectNo,
      name: activeStandardRoomName,
    };
  }
);

export const selectActiveStandardRoomArgs = createSelector(
  [selectProjectNo, selectActiveStandardRoomName],
  (projectNo, activeStandardRoomName) => {
    if (!projectNo || !activeStandardRoomName) {
      return null;
    }

    return {
      projectNo: Number(projectNo),
      roomNumber: activeStandardRoomName,
    };
  }
);

export const selectCachedActiveStandardRoomMcbimSpace = createSelector(
  [selectActiveStandardRoomMcbimSpaceArgs, (state: IRootState) => state],
  (args, state) => {
    if (!args) return null;

    return openapi.endpoints.getMcbimSingleMcbimSpace.select(args)(state).data ?? null;
  }
);

export const selectActiveStandardRoomMcbimItemsArgs = createSelector(
  [selectProjectNo, selectCachedActiveStandardRoomMcbimSpace],
  (projectNo, mcbimSpaceStandardRoom) => {
    if (!projectNo || !mcbimSpaceStandardRoom?._id) {
      return null;
    }

    return {
      projectNo,
      spaceId: String(mcbimSpaceStandardRoom._id),
    };
  }
);

export const selectCachedActiveStandardRoomMcbimItems = createSelector(
  [selectActiveStandardRoomMcbimItemsArgs, (state: IRootState) => state],
  (args, state) => {
    if (!args) return [];

    return openapi.endpoints.getMcbimItemsInSpace.select(args)(state).data ?? [];
  }
);

export const selectCachedActiveStandardRoom = createSelector(
  [selectActiveStandardRoomArgs, (state: IRootState) => state],
  (args, state) => {
    if (!args) return null;

    return openapi.endpoints.getMultiplacerSingleRoom.select(args)(state).data ?? null;
  }
);

export const selectActiveStandardRoomMmi = createSelector(
  [selectCachedActiveStandardRoomMcbimSpace, selectMmiPath],
  (mcbimSpaceStandardRoom, mmiPath): number | null => {
    if (!mcbimSpaceStandardRoom || !mmiPath) return null;

    const rawMmi = getDeeplyNestedValue(mcbimSpaceStandardRoom, mmiPath);
    if (rawMmi == null) return null;

    const mmi = typeof rawMmi === "number" ? rawMmi : Number(String(rawMmi).trim());
    return Number.isFinite(mmi) ? mmi : null;
  }
);

export const selectActiveStandardRoomMcbimItemsWithCategories = createSelector(
  [selectCachedActiveStandardRoomMcbimItems, selectCompCodePath],
  (mcbimItems, compCodePath): IMcBimItemWithExtraFrontendInfo[] => {
    return mcbimItems.map((item) => {
      return {
        ...item,
        deviceCategory: getDeviceCategoryForMcBimItem(
          getMcbimItemClassificationInput({
            mcbimItem: item,
            compCodePath,
          })
        ),
      };
    });
  }
);

export const selectActiveStandardRoomItemsWithCategories = createSelector(
  [selectActiveStandardRoomMcbimItemsWithCategories],
  (mcbimItems): IMcBimItemWithExtraFrontendInfo[] => {
    return mcbimItems.filter((item) => item.deviceCategory != null);
  }
);

export const selectActiveStandardRoomPixPolygon = createSelector(
  [selectCachedActiveStandardRoomMcbimSpace],
  (mcbimSpaceStandardRoom): IPixPolygon | undefined => {
    if (!mcbimSpaceStandardRoom) {
      return undefined;
    }
    const polygon = convertMcBimSpaceToPixPolygon({
      mcBimSpace: mcbimSpaceStandardRoom,
      dragSelectSpaces: false,
      showText: false,
    });

    if (!polygon) {
      return undefined;
    }

    return {
      ...polygon,
      text: {
        ...polygon.text,
        show: false,
      },
    };
  }
);

export const selectActiveStandardRoomCeilingGridPixPaths = createSelector(
  [selectCachedActiveStandardRoomMcbimSpace],
  (mcbimSpaceStandardRoom): IPixPath[] => {
    if (!mcbimSpaceStandardRoom) {
      return [];
    }

    return buildCeilingGridPixPathsFromLines([mcbimSpaceStandardRoom]);
  }
);

export const selectActiveStandardRoomCeilingHeightAboveFloorMm = createSelector(
  [selectCachedActiveStandardRoomMcbimSpace],
  (mcbimSpaceStandardRoom): number | undefined => {
    return getHeightFromFirstCeilingGrid(mcbimSpaceStandardRoom?.ceilingGrids ?? []);
  }
);

export const selectActiveStandardRoomMcbimItemPixPolygons = createSelector(
  [
    selectActiveStandardRoomMcbimItemsWithCategories,
    selectCompCodePath,
    selectStandardRoomShowOnlyCeilingComponents,
    selectStandardRoomShowLengthComponents,
    selectStandardRoomShowFurniture,
  ],
  (
    items,
    compCodePath,
    showOnlyCeilingComponents,
    showLengthComponents,
    showFurniture
  ): IPixPolygon[] => {
    return items
      .filter((item) => {
        if (
          item.instanceClass === "IfcCovering" ||
          item.instanceClass === "IfcBuildingElementProxy"
        ) {
          return false;
        }

        if (showOnlyCeilingComponents && item.deviceCategory == null) {
          return false;
        }

        const isLengthComponent =
          LENGTH_COMP_CLASSES.includes(
            item.instanceClass as (typeof LENGTH_COMP_CLASSES)[number]
          ) ||
          ATTACHED_TO_LENGTH_COMP_CLASSES.includes(
            item.instanceClass as (typeof ATTACHED_TO_LENGTH_COMP_CLASSES)[number]
          );

        if (!showLengthComponents && isLengthComponent) {
          return false;
        }

        if (!showFurniture && item.instanceClass === "IfcFurniture") {
          return false;
        }

        return Boolean(item.bbox);
      })
      .sort((a, b) => {
        const zDiff = getMcbimItemDrawZ(a) - getMcbimItemDrawZ(b);
        if (zDiff !== 0) {
          return zDiff;
        }

        if (a.deviceCategory == null && b.deviceCategory != null) {
          return -1;
        }

        if (a.deviceCategory != null && b.deviceCategory == null) {
          return 1;
        }

        return 0;
      })
      .map((item) => {
        const color = item.deviceCategory
          ? (DEVICES_TYPES_SETTINGS[item.deviceCategory]?.color ?? "#000000")
          : "#ccc";
        const compCode = compCodePath ? getDeeplyNestedValue(item, compCodePath) : undefined;

        return {
          id: String(item._id),
          points: item.bbox!,
          fillColor: color,
          strokeColor: color,
          strokeWidth: "0.05",
          text: {
            show: true,
            values: [compCode ?? "-"],
            color: "#000000",
            fontSize: "1",
          },
          tooltip: [compCode, item.name, item.instanceClass, item.type?.name]
            .filter((value): value is string => Boolean(value))
            .join("\n"),
          isMoveable: false,
          isDragSelectable: false,
        };
      });
  }
);

export const selectActiveStandardRoomPixPolygons = createSelector(
  [selectActiveStandardRoomPixPolygon, selectActiveStandardRoomMcbimItemPixPolygons],
  (roomPolygon, mcbimItemPolygons): IPixPolygon[] => {
    return [...(roomPolygon ? [roomPolygon] : []), ...mcbimItemPolygons];
  }
);

export const selectActiveStandardRoomDimensionLines = createSelector(
  [selectActiveStandardRoomPixPolygon, selectActiveDeviceInSelectedRoom],
  (roomPixPolygon, activeDevice): IPixDimensionLine[] => {
    if (activeDevice?.deviceCategory !== DeviceCategoryEnum.FIRE_SUPPRESSION_DEVICE) {
      return [];
    }

    const coordinate = activeDevice?.coordinate;
    const points = roomPixPolygon?.points;

    if (coordinate == null || points == null || coordinate.length < 2 || points.length < 3) {
      return [];
    }

    const point: [number, number] = [coordinate[0], coordinate[1]];

    if (!Number.isFinite(point[0]) || !Number.isFinite(point[1])) {
      return [];
    }

    return buildDimensionLinesFromPointToPolygonSides(points as [number, number][], point).map(
      (item) => item.dimensionLine
    );
  }
);

export const selectActiveStandardRoomExtent = createSelector(
  [selectActiveStandardRoomPixPolygons],
  (polygons) => {
    if (!polygons.length) return null;

    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    for (const polygon of polygons) {
      for (const point of polygon.points ?? []) {
        const x = point[0];
        const y = point[1];
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }

    if (
      !Number.isFinite(minX) ||
      !Number.isFinite(minY) ||
      !Number.isFinite(maxX) ||
      !Number.isFinite(maxY)
    ) {
      return null;
    }

    return {
      minX,
      minY,
      maxX,
      maxY,
      width: maxX - minX,
      height: maxY - minY,
    };
  }
);

export const selectActiveStandardRoomExtentKey = createSelector(
  [selectActiveStandardRoomExtent],
  (extent) => {
    if (!extent) return null;

    const round = (value: number) => Math.round(value * 100) / 100;
    return `${round(extent.minX)}:${round(extent.minY)}:${round(extent.maxX)}:${round(extent.maxY)}`;
  }
);
